import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { HERO_LINES, IMAGES } from "@/lib/content";
import { easeSignature, maskLine } from "@/lib/motion";

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ["start start", "end start"],
  });
  const y = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const scale = useTransform(scrollYProgress, [0, 1], [1.05, 1.18]);
  const overlay = useTransform(scrollYProgress, [0, 1], [0.82, 0.97]);

  return (
    <section
      id="home"
      ref={ref}
      data-testid="hero-section"
      className="relative min-h-[100svh] w-full overflow-hidden bg-ink-950"
    >
      {/* Parallax background */}
      <motion.div style={{ y, scale }} className="absolute inset-0 will-change-transform opacity-60">
        <img
          src={IMAGES.hero}
          alt="Mumbai skyline at night"
          className="h-full w-full object-cover"
          loading="eager"
        />
      </motion.div>
      <motion.div style={{ opacity: overlay }} className="absolute inset-0 bg-ink-950" />
      <div className="absolute inset-0 bg-gradient-to-b from-ink-950/60 via-ink-950/30 to-ink-950" />

      {/* Architectural grid lines */}
      <div className="pointer-events-none absolute inset-0 hidden md:grid grid-cols-12 max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16">
        {Array.from({ length: 11 }).map((_, i) => (
          <div key={i} className="border-l border-ivory/[0.05] col-span-1 h-full" />
        ))}
      </div>

      <div className="relative z-10 max-w-[1600px] mx-auto px-6 sm:px-10 lg:px-16 pt-32 md:pt-40 pb-16 min-h-[100svh] flex flex-col">
        {/* Meta line */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.6, duration: 1 }}
          className="flex items-center gap-3 font-mono-caps text-[10px] text-copper mb-10"
        >
          <span className="h-px w-8 bg-copper" />
          Mumbai · India · Est. Practice
        </motion.div>

        {/* Name — small label */}
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.8, ease: easeSignature }}
          className="text-ivory/70 font-mono-caps text-[11px] mb-6"
          data-testid="hero-eyebrow"
        >
          Parin Majithia · Architect · Real Estate Developer · Entrepreneur
        </motion.p>

        {/* Kinetic masked reveal headline */}
        <h1
          className="font-serif text-ivory leading-[0.98] tracking-tight text-[42px] sm:text-6xl md:text-7xl lg:text-[104px] max-w-[18ch]"
          data-testid="hero-headline"
        >
          {HERO_LINES.map((line, i) => (
            <span key={i} className="mask-line block">
              <motion.span
                variants={maskLine}
                initial="hidden"
                animate="visible"
                custom={i}
                className={i === HERO_LINES.length - 1 ? "italic text-copper" : ""}
              >
                {line}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.p
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.3, duration: 1, ease: easeSignature }}
          className="mt-10 max-w-xl text-ivory/70 text-base sm:text-lg leading-relaxed font-light"
          data-testid="hero-sub"
        >
          A second-generation real estate professional combining interior design,
          architecture, technology and development to create meaningful spaces in Mumbai.
        </motion.p>

        {/* CTAs */}
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 1.6, duration: 1, ease: easeSignature }}
          className="mt-12 flex flex-wrap gap-3 sm:gap-4"
        >
          <a
            href="#journey"
            data-testid="hero-cta-journey"
            className="group inline-flex items-center gap-3 border border-ivory/25 hover:border-copper text-ivory hover:text-copper px-6 py-4 font-mono-caps text-[11px] transition-colors duration-300"
          >
            Explore My Journey
            <span className="inline-block transition-transform duration-500 group-hover:translate-x-1">→</span>
          </a>
          <a
            href="#ami-one"
            data-testid="hero-cta-amione"
            className="group inline-flex items-center gap-3 bg-copper hover:bg-copper-hover text-ink-950 px-6 py-4 font-mono-caps text-[11px] transition-colors duration-300"
          >
            View AMI ONE
            <span className="inline-block transition-transform duration-500 group-hover:translate-x-1">→</span>
          </a>
          <a
            href="#contact"
            data-testid="hero-cta-connect"
            className="group inline-flex items-center gap-3 text-ivory hover:text-copper px-2 py-4 font-mono-caps text-[11px] link-underline transition-colors duration-300"
          >
            Connect With Me
          </a>
        </motion.div>

        {/* Bottom meta grid */}
        <div className="mt-auto pt-16 grid grid-cols-2 md:grid-cols-4 gap-6 border-t border-ivory/[0.08] pt-8">
          {[
            ["Practice", "Architecture · Development"],
            ["Base", "Mumbai · India"],
            ["Now", "AMI ONE — Borivali W."],
            ["Recognition", "'25 · '26 Awards"],
          ].map(([k, v], i) => (
            <motion.div
              key={k}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.8 + i * 0.1, duration: 0.8, ease: easeSignature }}
            >
              <div className="font-mono-caps text-[9px] text-copper mb-2">{k}</div>
              <div className="text-ivory text-sm">{v}</div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 0.5 }}
        transition={{ delay: 2.4, duration: 1 }}
        className="absolute bottom-4 left-1/2 -translate-x-1/2 font-mono-caps text-[9px] text-ivory/50 hidden md:block"
      >
        Scroll ↓
      </motion.div>
    </section>
  );
}
